import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { maxBotUsername } from '@/shared/brand'
import { useFeed, useRecordAction, FeedProfile } from '@/shared/hooks/useFeed'
import { useMatches } from '@/shared/hooks/useMatches'
import { SwipeStack } from '@/shared/components/SwipeStack'
import { bridge } from '@/shared/bridge'
import { SkeletonCard } from '@/shared/components/Skeleton'

export function Feed() {
  const navigate = useNavigate()
  const { data: feedProfiles, isLoading } = useFeed()
  const { data: matches } = useMatches()
  const recordAction = useRecordAction()
  const [swipeError, setSwipeError] = useState<string | null>(null)
  const [matchModal, setMatchModal] = useState<{
    name: string
    id: string
    partnerMaxProfileUrl: string | null
  } | null>(null)

  const handleSwipeFailure = useCallback((err: unknown) => {
    const msg =
      err instanceof Error
        ? err.message
        : typeof err === 'string'
          ? err
          : 'Не удалось сохранить свайп. Проверь сеть и попробуй ещё раз.'
    setSwipeError(msg)
    bridge.haptic('error')
  }, [])

  const handleLike = async (profile: FeedProfile) => {
    setSwipeError(null)
    try {
      const result = (await recordAction.mutateAsync({
        toUserId: profile.userId,
        action: 'LIKE',
      })) as { match?: { id: string; partnerMaxProfileUrl?: string | null } | null }

      if (result?.match) {
        setMatchModal({
          name: profile.name,
          id: result.match.id,
          partnerMaxProfileUrl: result.match.partnerMaxProfileUrl ?? null,
        })
        bridge.haptic('success')
      }
    } catch (e) {
      handleSwipeFailure(e)
      throw e
    }
  }

  const handleSkip = async (profile: FeedProfile) => {
    setSwipeError(null)
    try {
      await recordAction.mutateAsync({
        toUserId: profile.userId,
        action: 'SKIP',
      })
    } catch (e) {
      handleSwipeFailure(e)
      throw e
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-white dark:bg-gray-900">
      <header className="shrink-0 border-b border-gray-200 px-3 py-2 dark:border-gray-700">
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="shrink-0 rounded-full px-2 py-1 text-xl leading-none text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800"
              aria-label="На главный экран"
            >
              ←
            </button>
            <p className="min-w-0 truncate text-[11px] leading-tight text-gray-500 dark:text-gray-400">
              Вправо — симпатия · Влево — пропуск
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {matches && matches.length > 0 && (
              <button
                type="button"
                onClick={() => navigate('/matches')}
                className="relative rounded-full bg-accent px-3 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90"
              >
                💬 {matches.length}
              </button>
            )}
          </div>
        </div>
      </header>

      <div className="flex min-h-0 flex-1 flex-col items-stretch justify-center p-3 pb-6 sm:p-4">
        {swipeError ? (
          <div
            className="mb-3 flex shrink-0 items-start justify-between gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200"
            role="alert"
          >
            <span className="min-w-0 flex-1">{swipeError}</span>
            <button
              type="button"
              className="shrink-0 rounded-lg px-2 py-0.5 font-medium text-red-700 hover:bg-red-100 dark:text-red-100 dark:hover:bg-red-900/50"
              onClick={() => setSwipeError(null)}
            >
              OK
            </button>
          </div>
        ) : null}
        {isLoading ? (
          <div className="flex h-[min(88dvh,44rem)] w-full max-w-md min-h-[22rem] flex-1 flex-col">
            <SkeletonCard />
          </div>
        ) : feedProfiles && feedProfiles.length > 0 ? (
          <div className="flex h-[min(88dvh,44rem)] w-full max-w-md min-h-[22rem] flex-1 flex-col">
            <SwipeStack profiles={feedProfiles} onLike={handleLike} onSkip={handleSkip} isLoading={isLoading} />
          </div>
        ) : (
          <div className="flex max-w-sm flex-1 flex-col justify-center px-4 text-center">
            <p className="mb-4 text-lg text-gray-600 dark:text-gray-400">Нет анкет в ленте</p>
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="rounded-full bg-accent px-6 py-2 font-semibold text-white hover:opacity-90"
            >
              На главный экран
            </button>
          </div>
        )}
      </div>

      {matchModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-8 text-center dark:bg-gray-800">
            <div className="mb-4 animate-bounce text-6xl">🎉</div>
            <h2 className="mb-2 text-2xl font-bold text-gray-900 dark:text-white">Взаимная симпатия</h2>
            <p className="mb-6 text-gray-600 dark:text-gray-400">У вас мэтч с {matchModal.name}</p>
            <button
              type="button"
              onClick={() => {
                setMatchModal(null)
                bridge.openMaxLink(`https://max.ru/@${maxBotUsername()}?startapp=chat_${matchModal.id}`)
              }}
              className="mb-3 w-full rounded-lg bg-accent py-3 font-semibold text-white transition-opacity hover:opacity-90"
            >
              Написать сообщение →
            </button>
            {matchModal.partnerMaxProfileUrl ? (
              <button
                type="button"
                onClick={() => {
                  bridge.openMaxLink(matchModal.partnerMaxProfileUrl!)
                }}
                className="mb-3 w-full rounded-lg border border-gray-300 py-3 font-semibold text-gray-900 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:text-white dark:hover:bg-gray-700/80"
              >
                Профиль в MAX
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => setMatchModal(null)}
              className="w-full rounded-lg bg-gray-200 py-3 font-semibold text-gray-900 transition-colors hover:bg-gray-300 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
            >
              Продолжить
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
